export type SyncOperation = 'create' | 'update' | 'delete';

export interface SyncQueueItem {
  id: string;
  collection: string;
  documentId: string;
  operation: SyncOperation;
  data: Record<string, unknown>;
  attempts: number;
  lastAttemptAt: string | null;
  createdAt: string;
}

const QUEUE_STORAGE_KEY = 'lancarpos_sync_queue';
const MAX_ATTEMPTS = 5;
const RETRY_DELAYS = [60000, 120000, 300000, 600000, 1800000];

export function getQueue(): SyncQueueItem[] {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as SyncQueueItem[];
  } catch {
    return [];
  }
}

export function saveQueue(queue: SyncQueueItem[]): void {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('Failed to save sync queue:', error);
  }
}

export function addToQueue(
  collection: string,
  documentId: string,
  operation: SyncOperation,
  data: Record<string, unknown>
): SyncQueueItem {
  const queue = getQueue();
  
  const existingIndex = queue.findIndex(
    item => item.collection === collection && item.documentId === documentId
  );
  
  const now = new Date().toISOString();
  
  const item: SyncQueueItem = {
    id: existingIndex >= 0 ? queue[existingIndex].id : crypto.randomUUID(),
    collection,
    documentId,
    operation,
    data,
    attempts: existingIndex >= 0 ? queue[existingIndex].attempts : 0,
    lastAttemptAt: existingIndex >= 0 ? queue[existingIndex].lastAttemptAt : null,
    createdAt: existingIndex >= 0 ? queue[existingIndex].createdAt : now,
  };
  
  if (existingIndex >= 0) {
    queue[existingIndex] = item;
  } else {
    queue.push(item);
  }
  
  saveQueue(queue);
  return item;
}

export function removeFromQueue(itemId: string): void {
  const queue = getQueue();
  const filtered = queue.filter(item => item.id !== itemId);
  saveQueue(filtered);
}

export function incrementAttempts(itemId: string): SyncQueueItem | null {
  const queue = getQueue();
  const index = queue.findIndex(item => item.id === itemId);
  
  if (index < 0) return null;
  
  queue[index].attempts += 1;
  queue[index].lastAttemptAt = new Date().toISOString();
  saveQueue(queue);
  
  return queue[index];
}

export function getPendingItems(): SyncQueueItem[] {
  const queue = getQueue();
  const now = Date.now();
  
  return queue.filter(item => {
    if (item.attempts >= MAX_ATTEMPTS) return false;
    
    if (item.lastAttemptAt) {
      const lastAttempt = new Date(item.lastAttemptAt).getTime();
      const delay = RETRY_DELAYS[Math.min(item.attempts, RETRY_DELAYS.length - 1)];
      if (now - lastAttempt < delay) return false;
    }
    
    return true;
  });
}

export function getFailedItems(): SyncQueueItem[] {
  const queue = getQueue();
  return queue.filter(item => item.attempts >= MAX_ATTEMPTS);
}

export function getQueueStats(): { pending: number; failed: number; total: number } {
  const queue = getQueue();
  return {
    pending: getPendingItems().length,
    failed: getFailedItems().length,
    total: queue.length,
  };
}

export function clearQueue(): void {
  localStorage.removeItem(QUEUE_STORAGE_KEY);
}

export function retryFailedItems(): SyncQueueItem[] {
  const queue = getQueue();
  const failed = getFailedItems();
  
  failed.forEach(item => {
    item.attempts = 0;
    item.lastAttemptAt = null;
  });
  
  saveQueue(queue);
  return failed;
}
